import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import AuthLayout from '../components/AuthLayout';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const forgotPassword = useAuthStore((s) => s.forgotPassword);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      await forgotPassword(email);
      // Toast shown inside authStore.forgotPassword()
      setSent(true);
    } catch {
      // Error toast shown inside authStore.forgotPassword()
    } finally {
      setIsSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title="Check your email" subtitle="We've sent you a password reset link">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-6"
        >
          <div className="flex justify-center">
            <CheckCircle2 size={48} className="text-gpt-green" />
          </div>
          <p
            className={`text-sm ${
              theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
            }`}
          >
            If an account exists for{' '}
            <strong
              className={theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'}
            >
              {email}
            </strong>
            , you will receive a password reset email shortly.
          </p>
          <Link to="/login" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft size={16} />
            Back to login
          </Link>
        </motion.div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle="Enter your email to receive a reset link">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm"
          >
            {error}
          </motion.div>
        )}

        <div>
          <label
            htmlFor="email"
            className={`block text-sm font-medium mb-1.5 ${
              theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
            }`}
          >
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field"
            placeholder="Enter your email"
            required
            autoFocus
            autoComplete="email"
          />
        </div>

        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          type="submit"
          disabled={isSubmitting || !email}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Sending...
            </>
          ) : (
            'Send reset link'
          )}
        </motion.button>

        <p
          className={`text-center text-sm ${
            theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
          }`}
        >
          <Link
            to="/login"
            className="text-gpt-green hover:text-gpt-green-hover transition-colors font-medium inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} />
            Back to login
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;